import React, { useEffect, useState } from 'react';
import {
  Flex,
  GoodsCarousel,
  GoodsCarouselSkeleton,
  Headline,
  List,
  useSafeArea,
} from '@banhala/ui-kit';
import { useGoodsLikeRecommend } from '../queries/useGoodsLikeRecommend';
import { useAddLikeGoods, useLikeMap } from 'shared/src/hooks';
import { useLogDisplayGoods } from '../../../../../hooks/useLogDisplayGoods';
import { bridge, useNavigator } from 'shared/src/utils';
import type { LogAnalyticsEventParams } from 'shared/src/utils/bridge/logAnalyticsEvent/types';
import { useRemoveLikeGoods } from '../../../queries/useRemoveLikeGoods';

const GoodsLikeRecommendContent: React.FC<{
  scrollEnabled: boolean;
}> = props => {
  const { data: goodsLikeRecommend } = useGoodsLikeRecommend();
  const { logDisplayGoods } = useLogDisplayGoods({ enabled: true });
  const { bottom } = useSafeArea();

  const { likeMap, setLikeMap } = useLikeMap(
    goodsLikeRecommend.carousels
      .map(carousel => carousel.item_list)
      .flat(1)
      .reduce((acc, val) => {
        return {
          ...acc,
          [val.item.sno]: val.item.like.is_liked,
        };
      }, {}) ?? {},
  );

  const { addLikeGoods } = useAddLikeGoods({
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen: 'GOODS_DETAIL',
  });
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { navigator } = useNavigator();
  const [contentHeight, setContentHeight] = React.useState(0);

  return (
    <List
      style={{ height: contentHeight }}
      data={goodsLikeRecommend.carousels}
      onContentSizeChange={(w, h) => {
        setContentHeight(h);
      }}
      contentContainerStyle={{
        paddingBottom: bottom,
      }}
      scrollEnabled={props.scrollEnabled}
      // scrollEnabled={false}
      renderItem={({ item: carousel }) => {
        return (
          <Flex>
            <Headline
              headline={carousel.title}
              size={'medium'}
              topCompact={true}
            />
            <Flex>
              <GoodsCarousel
                list={carousel.item_list}
                onPressItem={async (pressed, index) => {
                  const { item, logging } = pressed;
                  bridge.logAnalyticsEvent({
                    name: 'CLICK_GOODS',
                    analytics: {
                      SCREEN: 'GOODS_DETAIL',
                      ...logging.analytics,
                      INDEX: index,
                    },
                  });
                  navigator.moveToGoods({
                    sno: item.sno,
                    firstPageRendering: item.first_page_rendering,
                  });
                }}
                onPressLike={async (pressed, index) => {
                  const { item, logging } = pressed;

                  const analytics: LogAnalyticsEventParams['analytics'] = {
                    SCREEN: 'GOODS_DETAIL',
                    ...logging.analytics,
                    INDEX: index,
                  };

                  if (!likeMap[item.sno]) {
                    bridge.logAnalyticsEvent({
                      name: 'CLICK_LIKE_GOODS',
                      analytics,
                    });
                    // 좋아요 누르기
                    addLikeGoods(item.sno, {
                      SCREEN: 'GOODS_DETAIL',
                    });
                    return;
                  }

                  bridge.logAnalyticsEvent({
                    name: 'CLICK_UNLIKE_GOODS',
                    analytics,
                  });

                  await mutateRemoveLikeGoods({
                    sno: item.sno,
                  });

                  //해제하기
                  bridge.likeGoods({
                    goods_sno: item.sno,
                    is_liked: false,
                  });
                  setLikeMap(prevState => ({
                    ...prevState,
                    [item.sno]: false,
                  }));
                }}
                size={'medium'}
                onViewableItemsChanged={logDisplayGoods}
              />
            </Flex>
          </Flex>
        );
      }}
    />
  );
};

const GoodsLikeRecommendSkeleton = () => (
  <Flex>
    <Flex padding={{ horizontal: 16, vertical: 16 }}>
      <Flex width={300} height={20} backgroundColor={'bg_tertiary'} />
    </Flex>
    <Flex height={200}>
      <GoodsCarouselSkeleton size={'small'} count={4} />
    </Flex>
    <Flex padding={{ horizontal: 16, vertical: 16 }}>
      <Flex width={300} height={20} backgroundColor={'bg_tertiary'} />
    </Flex>
    <Flex height={200}>
      <GoodsCarouselSkeleton size={'small'} count={4} />
    </Flex>
  </Flex>
);

export const GoodsLikeRecommend: React.FC<{
  scrollEnabled: boolean;
}> = props => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 300);
    return () => clearTimeout(timer);
  }, []);

  if (!ready) {
    return <GoodsLikeRecommendSkeleton />;
  }

  return (
    <React.Suspense fallback={<GoodsLikeRecommendSkeleton />}>
      <GoodsLikeRecommendContent {...props} />
    </React.Suspense>
  );
};
